import { forwardRef, useImperativeHandle, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical, faPen, faXmark, faCircleXmark, faSpinner } from '@fortawesome/pro-light-svg-icons';
import { CrmStatusModel } from '../../models/CRM';
import { useToast } from '@shared/hooks/use-toast';
import { useCrmStatus, useCreateStatus, useUpdateStatus, useDeleteStatus } from '../../hooks/useCrmStatus';
import { useCanManageCRM } from '@modules/CRM/shared/hooks/useCRMSettings';
import { ColorPicker } from '../interaction/ColorPicker';
import { getTextColorForBackground } from '../../utils/colorUtils';

export interface ManageStatusesModalRef {
  open: () => void;
}

interface ManageStatusesModalProps {
  onSuccess?: () => void;
}

export const ManageStatusesModal = forwardRef<ManageStatusesModalRef, ManageStatusesModalProps>(
  ({ onSuccess }, ref) => {
    const { t } = useTranslation('crmModule');
    const { toast } = useToast();
    const modalRef = useRef<ModalCenterRef>(null);
    const deleteConfirmRef = useRef<ConfirmModalRef>(null);

    const { statusList, isLoading, refetch } = useCrmStatus();
    const createStatusMutation = useCreateStatus();
    const updateStatusMutation = useUpdateStatus();
    const deleteStatusMutation = useDeleteStatus();
    const { canManage } = useCanManageCRM();

    const [editingId, setEditingId] = useState<number | null>(null);
    const [newName, setNewName] = useState('');
    const [newColor, setNewColor] = useState('#000000');
    const [editingName, setEditingName] = useState('');
    const [editingColor, setEditingColor] = useState('#000000');
    const [deletingId, setDeletingId] = useState<number | null>(null);

    useImperativeHandle(ref, () => ({
      open: () => {
        setNewName('');
        setNewColor('#000000');
        setEditingId(null);
        setDeletingId(null);
        modalRef.current?.openModal();
        refetch();
      },
    }));

    const handleCreate = useCallback(async () => {
      if (!newName.trim()) {
        toast({
          title: t('statuses.createError.title', 'Error al crear estado'),
          description: t('statuses.createError.nameRequired', 'El nombre es requerido'),
          variant: 'destructive',
        });
        return;
      }

      try {
        await createStatusMutation.mutateAsync({
          name: newName.trim(),
          color: newColor,
        });
        toast({
          title: t('statuses.createSuccess.title', 'Estado creado'),
          description: t('statuses.createSuccess.description', 'El estado se ha creado correctamente.'),
          variant: 'default',
        });
        setNewName('');
        setNewColor('#000000');
        refetch();
        onSuccess?.();
      } catch (error: any) {
        toast({
          title: t('statuses.createError.title', 'Error al crear estado'),
          description:
            error?.response?.data?.message ||
            error?.message ||
            t('statuses.createError.description', 'Error inesperado.'),
          variant: 'destructive',
        });
      }
    }, [newName, newColor, createStatusMutation, t, toast, refetch, onSuccess]);

    const handleStartEdit = useCallback((status: CrmStatusModel) => {
      // No permitir editar estados fijos (sin team_id)
      if (status.team_id == null) {
        return;
      }
      setEditingId(status.id);
      setEditingName(status.name);
      setEditingColor(status.color || '#000000');
    }, []);

    const handleCancelEdit = useCallback(() => {
      setEditingId(null);
      setEditingName('');
      setEditingColor('#000000');
    }, []);

    const handleSaveEdit = useCallback(
      async (statusId: number) => {
        if (!editingName.trim()) {
          toast({
            title: t('statuses.updateError.title', 'Error al actualizar estado'),
            description: t('statuses.updateError.nameRequired', 'El nombre es requerido'),
            variant: 'destructive',
          });
          return;
        }

        try {
          await updateStatusMutation.mutateAsync({
            statusId,
            data: {
              name: editingName.trim(),
              color: editingColor,
            },
          });
          toast({
            title: t('statuses.updateSuccess.title', 'Estado actualizado'),
            description: t('statuses.updateSuccess.description', 'El estado se ha actualizado correctamente.'),
            variant: 'default',
          });
          setEditingId(null);
          refetch();
          onSuccess?.();
        } catch (error: any) {
          toast({
            title: t('statuses.updateError.title', 'Error al actualizar estado'),
            description:
              error?.response?.data?.message ||
              error?.message ||
              t('statuses.updateError.description', 'Error inesperado.'),
            variant: 'destructive',
          });
        }
      },
      [editingName, editingColor, updateStatusMutation, t, toast, refetch, onSuccess],
    );

    const handleDeleteClick = useCallback(
      (statusId: number) => {
        setDeletingId(statusId);
        const status = statusList.find((s) => s.id === statusId);
        deleteConfirmRef.current?.open(
          async () => {
            try {
              await deleteStatusMutation.mutateAsync(statusId);
              toast({
                title: t('statuses.deleteSuccess.title', 'Estado eliminado'),
                description: t('statuses.deleteSuccess.description', 'El estado se ha eliminado correctamente.'),
                variant: 'default',
              });
              setDeletingId(null);
              refetch();
              onSuccess?.();
            } catch (error: any) {
              toast({
                title: t('statuses.deleteError.title', 'Error al eliminar estado'),
                description:
                  error?.response?.data?.message ||
                  error?.message ||
                  t('statuses.deleteError.description', 'Error inesperado.'),
                variant: 'destructive',
              });
              setDeletingId(null);
            }
          },
          undefined,
          t('statuses.deleteConfirm.title', 'Eliminar estado'),
          t('statuses.deleteConfirm.message', '¿Estás seguro de que deseas eliminar este estado?').replace(
            '{{name}}',
            status?.name || '',
          ),
        );
      },
      [statusList, deleteStatusMutation, t, toast, refetch, onSuccess],
    );

    return (
      <>
        <ModalCenter
          ref={modalRef}
          title={t('statuses.modal.title', 'Estados')}
          type="default"
          width="lg"
          className="w-full sm:w-[600px] sm:max-h-[90vh]"
          fullScreenMobile={true}
        >
          {/* Crear estado nuevo */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h3 className="text-sm font-normal text-text">{t('statuses.createNew', 'Crear estado nuevo')}</h3>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <div className="relative flex-1 sm:flex-none">
                  <Input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder={t('statuses.namePlaceholder', 'Ej: Pendiente')}
                    className="w-full sm:w-auto sm:min-w-[200px] pr-9"
                    disabled={!canManage || createStatusMutation.isPending}
                    data-testid="status-name-input"
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <ColorPicker
                      value={newColor}
                      onChange={setNewColor}
                      disabled={!canManage || createStatusMutation.isPending}
                      size="sm"
                    />
                  </div>
                </div>
                <Button
                  onClick={handleCreate}
                  disabled={!canManage || createStatusMutation.isPending || !newName.trim()}
                  variant="outline"
                  className="shrink-0 h-10"
                  data-testid="create-status-button"
                >
                  {createStatusMutation.isPending ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="mr-2 w-3.5 h-3.5 animate-spin" />
                      {t('statuses.creating', 'Creando...')}
                    </>
                  ) : (
                    t('statuses.create', 'Crear')
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Separador */}
          <div className="border-t border-card-border mb-4" />

          {/* Lista de estados */}
          <div>
            <h3 className="text-sm font-normal text-text mb-3">{t('statuses.all', 'Todos')}</h3>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <FontAwesomeIcon icon={faSpinner} className="w-5 h-5 animate-spin text-icons" />
              </div>
            ) : statusList.length === 0 ? (
              <p className="text-sm text-complementary-text py-4">
                {t('statuses.empty', 'No hay estados creados. Crea uno con el formulario de arriba.')}
              </p>
            ) : (
              <div className="max-h-[400px] overflow-y-auto space-y-1">
                {statusList.map((status) => (
                  <div key={status.id}>
                    {editingId === status.id ? (
                      <div className="flex items-center gap-2 px-2 py-1 rounded-lg">
                        <FontAwesomeIcon icon={faPen} className="w-3.5 h-3.5 text-icons" />
                        <span className="text-sm text-icons shrink-0">{t('statuses.edit', 'Editar estado')}</span>
                        <div className="relative flex-1 min-w-0 ml-3">
                          <Input
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            className="w-full pr-9"
                            disabled={!canManage || updateStatusMutation.isPending}
                            data-testid={`edit-status-name-${status.id}`}
                          />
                          <div className="absolute right-2 top-1/2 -translate-y-1/2">
                            <ColorPicker
                              value={editingColor}
                              onChange={setEditingColor}
                              disabled={!canManage || updateStatusMutation.isPending}
                              size="sm"
                            />
                          </div>
                        </div>
                        <Button
                          onClick={() => handleSaveEdit(status.id)}
                          disabled={!canManage || updateStatusMutation.isPending || !editingName.trim()}
                          variant="outline"
                          className="shrink-0 h-10"
                          data-testid={`save-status-${status.id}`}
                        >
                          {updateStatusMutation.isPending ? (
                            <FontAwesomeIcon icon={faSpinner} className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            t('statuses.save', 'Guardar')
                          )}
                        </Button>
                        <Button
                          onClick={handleCancelEdit}
                          disabled={updateStatusMutation.isPending}
                          variant="ghost"
                          size="sm"
                          className="p-0 min-w-0"
                          data-testid={`cancel-edit-status-${status.id}`}
                        >
                          <FontAwesomeIcon icon={faXmark} className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-2 px-2 py-1 rounded-lg group hover:bg-hover-press transition-colors">
                        <div className="flex items-center gap-2">
                          {/* Solo mostrar icono de ordenar si tiene team_id */}
                          {status.team_id != null && (
                            <FontAwesomeIcon icon={faGripVertical} className="w-3 h-3 text-icons cursor-default" />
                          )}
                          {status.team_id == null && (
                            <span className="w-3 h-3" /> // Espaciador para alineación
                          )}
                          <span
                            className="w-fit px-3 py-0.5 rounded-full text-sm"
                            style={{
                              backgroundColor: status.color || '#000000',
                              color: getTextColorForBackground(status.color || '#000000'),
                            }}
                          >
                            {status.name}
                          </span>
                          {status.team_id == null && (
                            <span className="text-xs text-complementary-text italic ml-2">(Fijo)</span>
                          )}
                        </div>
                        {/* Solo mostrar botones de editar/eliminar si tiene team_id */}
                        {status.team_id != null && (
                          <div className="flex items-center gap-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={() => handleStartEdit(status)}
                              disabled={!canManage || deletingId === status.id || deleteStatusMutation.isPending}
                              className="px-2 hover:bg-hover-press rounded transition-colors disabled:opacity-50"
                              data-testid={`edit-status-${status.id}`}
                              aria-label={t('statuses.edit', 'Editar estado')}
                            >
                              <FontAwesomeIcon icon={faPen} className="w-3.5 h-3.5 text-icons" />
                            </button>
                            {deletingId === status.id ? (
                              <span className="px-2 opacity-100" aria-hidden>
                                <FontAwesomeIcon icon={faSpinner} className="w-3.5 h-3.5 animate-spin text-icons" />
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleDeleteClick(status.id)}
                                disabled={!canManage || deleteStatusMutation.isPending}
                                className="px-2 hover:bg-hover-press rounded transition-colors disabled:opacity-50"
                                data-testid={`delete-status-${status.id}`}
                                aria-label={t('statuses.delete', 'Eliminar estado')}
                              >
                                <FontAwesomeIcon icon={faCircleXmark} className="w-3.5 h-3.5 text-icons" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </ModalCenter>
        <ConfirmModal ref={deleteConfirmRef} />
      </>
    );
  },
);

ManageStatusesModal.displayName = 'ManageStatusesModal';
